/**
 * Filtering the model catalog (#1987).
 *
 * The catalog holds ~663 rows and, before this, two controls narrowed it: a
 * name box and an "In use only" checkbox. Every other column was sortable but
 * not filterable, so "cheap models that can call tools and take a big context"
 * meant sorting four times and reading.
 *
 * **Why a module rather than more page code.** This is pure row-matching logic
 * with no rendering, and it should be testable without mounting a 663-row
 * table. `Pricing/index.tsx` already owns fetching, state, sorting, rendering
 * and row editing; `agentModelSpec.ts` is spec parsing and the picker's fixed
 * facet chips, which are a different shape from composable conditions.
 *
 * **The joining rule is one word.** Conditions combine with AND and never OR.
 * Where a field genuinely needs alternatives, the *value* carries them — pick
 * Low and Medium on one Cost chip — so an operator never chooses an operator.
 * That keeps a filter bar from turning into a query builder, which nothing here
 * needs.
 */
import type { ModelPricingRow } from '@/lib/api'

/** Every field an operator can filter on — one per column the table shows. */
export type FilterFieldId =
  | 'name'
  | 'toolCalling'
  | 'vision'
  | 'reasoning'
  | 'cost'
  | 'provider'
  | 'source'
  | 'status'
  | 'origin'
  | 'inputCentsPer1M'
  | 'outputCentsPer1M'
  | 'cacheReadCentsPer1M'
  | 'cacheWriteCentsPer1M'
  | 'context'
  | 'added'

/** A single condition. Discriminated by `field`'s kind rather than carrying a
 * free-form operator, so an unrepresentable combination cannot be built. */
export type CatalogFilter =
  | { id: string; field: 'name'; text: string }
  /** Multi-value: the picked values are OR'd inside this one condition. */
  | {
      id: string
      field: 'toolCalling' | 'vision' | 'reasoning' | 'cost' | 'provider' | 'source' | 'status' | 'origin'
      values: readonly string[]
    }
  | {
      id: string
      field: 'inputCentsPer1M' | 'outputCentsPer1M' | 'cacheReadCentsPer1M'
        | 'cacheWriteCentsPer1M' | 'context'
      op: 'atLeast' | 'atMost'
      value: number
    }
  | { id: string; field: 'added'; op: 'after' | 'before'; value: string }

/**
 * Whether a maker's country is one the filter hides (#2015).
 *
 * Mirrors the server's `EXCLUDED_COUNTRIES` — the full 15 CFR § 791.4
 * designation — by hand, the same way the facet and cost-category literals are
 * mirrored; there is no shared package.
 *
 * All six, not just the ones with model makers today. A first draft carried
 * only China and Russia, which would have silently passed a Cuban or Iranian
 * maker the moment one appeared, while the tooltip beside it claimed otherwise
 * (PR #2020 Copilot).
 */
const EXCLUDED_COUNTRY_CODES: ReadonlySet<string> = new Set([
  'CN', // incl. the Hong Kong and Macau Special Administrative Regions
  'CU', 'IR', 'KP', 'RU',
  'VE', // the Maduro regime, as § 791.4 phrases it
])

export function isExcludedOriginCountry(country: string | undefined): boolean {
  // Absent or unclassified fails closed: the origin map trails upstream by
  // construction, and a maker nobody has classified is not evidence of safety.
  if (country === undefined || country === 'unknown') return true
  return EXCLUDED_COUNTRY_CODES.has(country)
}

/** What the predicate needs that a row does not carry.
 *
 * Passed in rather than recomputed here: `providerOf` is the same derivation
 * the Provider column and its sort already use, and "in use" lives in the
 * coverage response, not on the row. Duplicating either would let the filter
 * disagree with what the table displays. */
export interface FilterContext {
  providerOf: (row: ModelPricingRow) => string | null
  isInUse: (row: ModelPricingRow) => boolean
}

/** Operator-visible labels. Kept beside the ids so a chip, the field picker and
 * the predicate cannot drift apart. */
export const FIELD_LABELS: Record<FilterFieldId, string> = {
  name: 'Name',
  toolCalling: 'Tool calling',
  vision: 'Vision',
  reasoning: 'Reasoning',
  cost: 'Cost',
  provider: 'Provider',
  source: 'Source',
  status: 'Status',
  origin: 'Country of origin',
  inputCentsPer1M: 'Input ¢/1M',
  outputCentsPer1M: 'Output ¢/1M',
  cacheReadCentsPer1M: 'Cache read ¢/1M',
  cacheWriteCentsPer1M: 'Cache write ¢/1M',
  context: 'Context',
  added: 'Added',
}

/** Fields whose values are picked from a fixed list, with the list. `provider`
 * is absent on purpose — its choices come from the rows actually present. */
export const VALUE_CHOICES: Partial<Record<FilterFieldId, ReadonlyArray<{ value: string; label: string }>>> = {
  toolCalling: [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
    { value: 'unknown', label: 'Not known' },
  ],
  vision: [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
    { value: 'unknown', label: 'Not known' },
  ],
  reasoning: [
    { value: 'yes', label: 'Yes' },
    { value: 'alwaysOn', label: 'Always on' },
    { value: 'no', label: 'No' },
    { value: 'unknown', label: 'Not known' },
  ],
  origin: [
    { value: 'notExcluded', label: 'Not excluded' },
    { value: 'excluded', label: 'Excluded' },
  ],
  cost: [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'premium', label: 'Premium' },
  ],
  // `override` was a fourth source until #2337. Nothing writes it any more
  // and the boot migration moves the last rows off it, so the option would
  // always come back empty — the same dead-filter problem as `Pinned` above.
  source: [
    { value: 'openrouter', label: 'openrouter' },
    { value: 'litellm', label: 'litellm' },
    { value: 'ottocloud', label: 'ottocloud' },
    { value: 'discovery', label: 'discovery' },
  ],
  status: [
    { value: 'inUse', label: 'In use' },
    { value: 'unpriced', label: 'Unpriced' },
    { value: 'pending', label: 'Pending review' },
  ],
}

/** Threshold choices, so an operator picks a magnitude instead of typing a
 * token count. Context is asked as "at least" in practice — headroom is the
 * question — but both directions are offered for symmetry with the rates. */
export const CONTEXT_CHOICES: ReadonlyArray<{ value: number; label: string }> = [
  { value: 32_000, label: '32K' },
  { value: 100_000, label: '100K' },
  { value: 200_000, label: '200K' },
  { value: 1_000_000, label: '1M' },
]

/** Match a tri-state capability. `unknown` means the catalog said nothing —
 * never a row that positively reports "no". Collapsing those two would file
 * every model we have no information about under "cannot", which is the
 * distinction #1926 exists to preserve. */
function matchesTriState(value: boolean | undefined, picked: readonly string[]): boolean {
  return picked.some(p =>
    p === 'yes' ? value === true
      : p === 'no' ? value === false
        : value === undefined,
  )
}

/**
 * Reasoning, which is NOT a plain tri-state.
 *
 * `reasoningEffortReducible` answers "can the thinking be turned down", not
 * "does it think" — a non-reasoning model reports a definitive `false` for it
 * (see #1948). So the gate is `reasoning === true`, and "always on" is a
 * modifier within that, exactly as the badge renders it.
 *
 * `Yes` therefore INCLUDES always-on models and `Always on` returns a subset of
 * it. They are not disjoint: an operator asking for models that reason would be
 * badly surprised not to be shown the ones that reason hardest.
 */
function matchesReasoning(row: ModelPricingRow, picked: readonly string[]): boolean {
  return picked.some(p => {
    if (p === 'yes') return row.reasoning === true
    if (p === 'alwaysOn') return row.reasoning === true && row.reasoningEffortReducible === false
    if (p === 'no') return row.reasoning === false
    return row.reasoning === undefined
  })
}

/** Status, whose values are independent signals rather than values of one
 * field — a row can be in use and pending at once, which is why the table
 * draws those badges independently. Picking several matches a row carrying
 * ANY of them.
 *
 * `Pinned` was here until #2337. Nothing can be pinned any more, so a filter
 * for it would match nothing forever and read as a broken screen rather than
 * an empty one. */
function matchesStatus(row: ModelPricingRow, picked: readonly string[], ctx: FilterContext): boolean {
  return picked.some(p =>
    p === 'inUse' ? ctx.isInUse(row)
      : p === 'unpriced' ? row._unpriced === true
        : row.pendingProposedAt !== undefined,
  )
}

/** Does this row satisfy one condition? */
export function matchesFilter(
  row: ModelPricingRow,
  filter: CatalogFilter,
  ctx: FilterContext,
): boolean {
  switch (filter.field) {
    case 'name':
      return row.namePattern.toLowerCase().includes(filter.text.toLowerCase())
    case 'toolCalling':
      return matchesTriState(row.toolCalling, filter.values)
    case 'vision':
      return matchesTriState(row.imageInput, filter.values)
    case 'reasoning':
      return matchesReasoning(row, filter.values)
    case 'status':
      return matchesStatus(row, filter.values, ctx)
    case 'cost':
      return filter.values.includes(row.costCategory ?? '')
    case 'source':
      return filter.values.includes(row._source ?? '')
    case 'origin': {
      // #2015 — `notExcluded` is the only value the toggle writes. Phrased as
      // a value on a normal condition rather than a boolean field so the chip
      // reads like every other one and `Clear all` needs no special case.
      const excluded = isExcludedOriginCountry(row.origin?.country)
      return filter.values.includes(excluded ? 'excluded' : 'notExcluded')
    }
    case 'provider': {
      const p = ctx.providerOf(row)
      return p !== null && filter.values.includes(p)
    }
    case 'added': {
      // Absent means we never learned the date — not very old and not very new.
      // The column renders it as an em dash and the sort keeps undated rows
      // last in both directions, so neither "after" nor "before" returns them.
      if (!row.listedAt) return false
      const t = Date.parse(row.listedAt)
      const bound = Date.parse(filter.value)
      if (Number.isNaN(t) || Number.isNaN(bound)) return false
      return filter.op === 'after' ? t >= bound : t <= bound
    }
    default: {
      // The numeric thresholds. An absent rate is not zero — a model with no
      // cache-write rate has not been priced at zero for it — so it satisfies
      // neither direction, matching how the column renders and how it sorts.
      const n = filter.field === 'context' ? row.maxInputTokens : row[filter.field]
      if (typeof n !== 'number') return false
      return filter.op === 'atLeast' ? n >= filter.value : n <= filter.value
    }
  }
}

/** Does this row satisfy every condition? AND across conditions; the OR lives
 * inside a single multi-value condition. An empty list matches everything, so
 * the caller needs no special case for "no filters". */
export function matchesFilters(
  row: ModelPricingRow,
  filters: readonly CatalogFilter[],
  ctx: FilterContext,
): boolean {
  return filters.every(f => matchesFilter(row, f, ctx))
}

/** The chip's text. Built here rather than in the component so the wording
 * cannot drift from the predicate that enforces it. */
export function describeFilter(filter: CatalogFilter): string {
  const label = FIELD_LABELS[filter.field]
  if (filter.field === 'name') return `${label} contains "${filter.text}"`
  // The origin condition opts OUT of the shared `<field> is <value>` sentence
  // (#2110). Run through it, the stored `notExcluded` produced "Country of
  // origin is Not excluded" — a double negative that reads as the opposite of
  // what it does, as though origin were the thing NOT being excluded.
  //
  // It can opt out because it is the only condition nobody builds by hand:
  // `origin` is absent from the picker's field list and the toggle is its only
  // writer, so this branch is not one phrasing among several. Mirroring the
  // button beside it ("Hide excluded") also states the filter as an action in
  // force rather than a property of a row, which is what it is.
  //
  // Deliberately silent on the countries themselves and on unknown makers: the
  // tooltip enumerates both, and nothing here may imply the remaining models
  // were approved — see the `OriginToggle` docstring.
  if (filter.field === 'origin' && filter.values.length === 1 && filter.values[0] === 'notExcluded') {
    return 'Excluded countries hidden'
  }
  if (filter.field === 'added') {
    // Built from the parts rather than `new Date('2026-06-01')`, which parses
    // as UTC midnight and then formats in local time — so west of Greenwich the
    // chip would name the day BEFORE the one the operator picked, while the
    // filter matched the day they picked. A chip that disagrees with what it
    // filters is worse than no chip.
    const [y, m, d] = filter.value.split('-').map(Number)
    const when = y && m && d
      ? new Date(y, m - 1, d).toLocaleDateString(undefined, {
          year: 'numeric', month: 'short', day: 'numeric',
        })
      : filter.value
    return `${label} is ${filter.op === 'after' ? 'after' : 'before'} ${when}`
  }
  if ('op' in filter) {
    const amount = filter.field === 'context'
      ? (CONTEXT_CHOICES.find(c => c.value === filter.value)?.label ?? String(filter.value))
      : String(filter.value)
    return `${label} is ${filter.op === 'atLeast' ? 'at least' : 'at most'} ${amount}`
  }
  const choices = VALUE_CHOICES[filter.field]
  const words = filter.values.map(v => choices?.find(c => c.value === v)?.label ?? v)
  // "is Low or Medium" — the alternatives read as the value, which is what
  // keeps the joining rule between chips a single unambiguous "and".
  const joined = words.length <= 1
    ? words[0] ?? ''
    : `${words.slice(0, -1).join(', ')} or ${words[words.length - 1]}`
  return `${label} is ${joined}`
}
